import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/ui/table/DataTable";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import { displayValue } from "@/utils/Helper";
import { Checkbox } from "@/components/ui/CheckBox";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/UseToast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { ErrorType } from "@/app/types";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { Badge } from "@/components/ui/Badge";
import { DataTableRowActions } from "./data-table-row-action";
import { useGetEventsQuery } from "@/services/EventApi";
import { Event, EventStatus } from "@/app/types/event";
import EventForm from "../modal/form";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { EventStatusFilter } from "@/constants/event-management";


const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "created_at",
};

const EventTable = () => {
  const eventAccess = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).event ??
        "write" // Defaulting to write for now if not specified
      );
    } catch {
      return "write";
    }
  })();

  const { toast } = useToast();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [filterData, setFilterData] = useState({});
  const [data, setData] = useState<Event | undefined>(undefined);
  const [inputValue, setInputValue] = useState<string>("");
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.org_id) ?? 0;

  const {
    data: eventData,
    isLoading: isEventDataLoading,
    refetch,
    error,
    isError,
  } = useGetEventsQuery(
    {
      org_id: orgId,
      query: query,
    },
    {
      skip: !orgId || query == "",
    }
  );
  console.log({eventData})

  const tableData = useMemo(() => {
    return Array.isArray(eventData?.data) ? eventData.data : [];
  }, [eventData]);

  const handleOpenForm = (payload: Event) => {
    setAction("edit");
    setData({ ...payload, event_id: payload.event_id });
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<Event> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={eventAccess}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
      />
    ),
  };

  const columns: ColumnDef<Event>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      meta: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Event Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const Value = row?.original.name;
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {eventAccess === "write" ? (
              <button
                onClick={() => {
                  navigate(`/admin/events/${row.original.event_id}/tickets`);
                }}
                className="underline capitalize"
              >
                {displayValue(Value)}
              </button>
            ) : (
              displayValue(Value)
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "start_date",
      meta: "start_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Start Date"
          sortKey="start_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
          {displayValue(moment(row?.original.start_date).format("DD MMM YYYY"))}
        </div>
      ),
    },
    {
      accessorKey: "end_date",
      meta: "end_data",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="End Date"
          sortKey="end_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
          {displayValue(moment(row?.original.end_date).format("DD MMM YYYY"))}
        </div>
      ),
    },
    /* {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const statusColors: Record<string, string> = {
          [EventStatus.ACTIVE]: "bg-green-600",
          [EventStatus.INACTIVE]: "bg-red-500",
          [EventStatus.ARCHIVE]: "bg-yellow-500",
        };
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <Badge
              className={`${statusColors[row.original?.status] || "bg-gray-400"} text-white font-medium`}
            >
              {displayValue(row?.original.status)}
            </Badge>
          </div>
        );
      },
    }, */
    ...(eventAccess !== "read" ? [actionsColumn] : []),
  ];

  const totalRecords = eventData?.total_count ?? 0;

  useEffect(() => {
    if (isError) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ?? (typedError?.data as unknown as { message?: string })?.message}`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Event Status",
      options: EventStatusFilter,
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  return (
    <>
      <div className="flex flex-row lg:items-center justify-between gap-4 px-3 ">
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          placeHolder={"Search Events"}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />

        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <Button
            access={eventAccess !== "read"}
            className="bg-primary text-sm text-white flex items-center gap-1 lg:mb-0 h-8 px-2"
            onClick={handleOpen}
          >
            <i className="fa-solid fa-plus" />
            Create Event
          </Button>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="mt-4 bg-white">
        <DataTable
          columns={columns}
          data={tableData}
          isLoading={isEventDataLoading}
          searchMessage="No Events Found"
        />
      </div>

      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        setInputValue={setInputValue}
        filterDisplay={filterDisplay}
      />

      <EventForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </>
  );
};

export default EventTable;
